import { AudioEngine } from './audio'
import type { DecodedBank } from './audio'
import { TransportClock } from './clock'
import { LOOP_SECONDS, TEMPO_PRESETS, TRACK_COUNT, VIDEO_SECONDS } from './constants'
import type { TempoPercent } from './constants'
import { posterUrl, selectVideoTiers, stemUrls } from './media'
import { MosaicRenderer } from './renderer'
import { ElementFrameSource } from './video/fallback'
import { FrameScheduler } from './video/scheduler'
import type { FrameSource, SyncDiagnostics } from './video/source'

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

export type StatusKind = 'info' | 'good' | 'error'

export interface PlayerEvents {
  status(message: string, kind?: StatusKind): void
  time(musical: number): void
  changed(): void
}

export interface PlayerState {
  ready: boolean
  playing: boolean
  busy: boolean
  tempoPercent: TempoPercent
  loopEnabled: boolean
  loopStart: number
  loopEnd: number
  selected: number
  muted: readonly boolean[]
  diagnostics: SyncDiagnostics | null
}

export class Player {
  private audio: AudioEngine | null = null
  private clock: TransportClock | null = null
  private video: FrameSource | null = null
  private videoElement: HTMLVideoElement | null = null
  private poster: HTMLImageElement | null = null
  private bank: DecodedBank | null = null

  private raf = 0
  private refreshInterval = 1 / 60
  private lastRafTime = 0
  private videoFailures = 0
  private hasLiveFrame = false

  private ready = false
  private busy = false
  private tempoPercent: TempoPercent = 100
  private loopEnabled = true
  private loopStart = 0
  private loopEnd = LOOP_SECONDS
  private selected = 0
  private readonly muted: boolean[] = Array.from({ length: TRACK_COUNT }, () => false)

  constructor(
    private readonly renderer: MosaicRenderer,
    private readonly events: PlayerEvents,
  ) {
    this.loadPoster()
    this.startRenderLoop()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  get state(): PlayerState {
    return {
      ready: this.ready,
      playing: this.clock?.isPlaying ?? false,
      busy: this.busy,
      tempoPercent: this.tempoPercent,
      loopEnabled: this.loopEnabled,
      loopStart: this.loopStart,
      loopEnd: this.loopEnd,
      selected: this.selected,
      muted: this.muted,
      diagnostics: this.video?.diagnostics ?? null,
    }
  }

  get position(): number {
    return this.clock?.positionAudible() ?? 0
  }

  /**
   * Must be called from inside a user gesture. Prepares audio, decodes the
   * selected bank and brings up the video path.
   */
  async prepare(): Promise<boolean> {
    if (this.ready) return true
    if (this.busy) return false
    this.busy = true
    this.events.changed()

    try {
      const audio = this.audio ?? AudioEngine.create()
      this.audio = audio
      await audio.unlock()

      this.clock ??= new TransportClock(audio.ctx)
      this.clock.setLoop({ start: this.loopStart, end: this.loopEnd }, this.loopEnabled)

      this.events.status(`Preparing ${this.tempoPercent}% audio…`)
      const bank = await audio.loadBank(this.tempoPercent, ({ loaded, total }) => {
        this.events.status(`Preparing ${this.tempoPercent}% audio: ${loaded} of ${total}…`)
      })
      audio.applyNormalisation(bank)
      this.bank = bank

      for (let i = 0; i < TRACK_COUNT; i++) audio.setMute(i, this.muted[i]!)

      this.events.status('Preparing video…')
      await this.setupVideo()

      this.ready = true
      this.events.status(
        this.video?.kind === 'frame-exact'
          ? 'Ready. Video is frame-locked to the audio clock.'
          : 'Ready. Video is phase-locked to the audio clock.',
        'good',
      )
      this.prefetchOtherBanks()
      return true
    } catch (err) {
      this.events.status(err instanceof Error ? err.message : String(err), 'error')
      return false
    } finally {
      this.busy = false
      this.events.changed()
    }
  }

  async togglePlay(): Promise<void> {
    if (this.clock?.isPlaying) {
      this.pause()
      return
    }
    if (!this.ready && !(await this.prepare())) return
    this.play()
  }

  play(): void {
    const { audio, clock, bank } = this
    if (!audio || !clock || !bank || clock.isPlaying) return

    const at = clock.positionAudible()
    const when = audio.start(bank, at, clock.currentTempo, {
      enabled: this.loopEnabled,
      start: this.loopStart,
      end: this.loopEnd,
    })
    clock.start(when, at)

    if (!this.loopEnabled) audio.onEnded(() => this.pause())

    this.video?.seek(at)
    this.events.status(`Playing at ${this.tempoPercent}%.`, 'good')
    this.events.changed()
  }

  pause(): void {
    const { audio, clock } = this
    if (!audio || !clock || !clock.isPlaying) return
    clock.stop()
    audio.stopSources()
    this.events.changed()
  }

  stop(): void {
    const { audio, clock } = this
    if (!audio || !clock) return
    clock.stop()
    audio.stopSources()
    this.seek(this.loopEnabled ? this.loopStart : 0)
    this.events.changed()
  }

  seek(musical: number): void {
    const clock = this.clock
    if (!clock) return
    const wasPlaying = clock.isPlaying
    if (wasPlaying) this.pause()
    clock.seek(musical)
    this.video?.seek(clock.positionAudible())
    this.renderer.invalidate()
    this.events.time(clock.positionAudible())
    if (wasPlaying) this.play()
  }

  /**
   * Switch tempo without interrupting playback more than the audio restart
   * requires.
   *
   * The video is deliberately untouched. Because frames are addressed by
   * musical position rather than by wall-clock playback rate, a tempo change is
   * invisible to the video path — it simply walks the same frame index at a
   * different speed. The previous build tore down the transport, re-seeked the
   * remote video and waited on it, which is what produced the freeze this
   * replaces.
   */
  async setTempo(percent: TempoPercent): Promise<void> {
    if (percent === this.tempoPercent) return
    const { audio, clock } = this
    if (!audio || !clock || !this.ready) {
      this.tempoPercent = percent
      this.events.status(`${percent}% selected. Press play to prepare audio.`)
      this.events.changed()
      return
    }
    if (this.busy) return

    this.busy = true
    this.events.changed()
    const wasPlaying = clock.isPlaying

    try {
      // Prepare the new bank while the current one keeps playing.
      if (wasPlaying) {
        this.events.status(`Preparing ${percent}% in the background — playback continues…`)
      }
      const bank = await audio.loadBank(percent, ({ loaded, total }) => {
        if (!wasPlaying) this.events.status(`Preparing ${percent}% audio: ${loaded} of ${total}…`)
      })

      const at = clock.positionAudible()
      if (wasPlaying) {
        clock.stop()
        audio.stopSources()
      }

      this.bank = bank
      this.tempoPercent = percent
      clock.setTempo(percent / 100)
      clock.seek(at)

      if (wasPlaying) {
        const when = audio.start(bank, at, clock.currentTempo, {
          enabled: this.loopEnabled,
          start: this.loopStart,
          end: this.loopEnd,
        })
        clock.start(when, at)
      }
      this.events.status(`${percent}% tempo ready.`, 'good')
    } catch (err) {
      this.events.status(
        `Tempo could not be prepared: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      )
    } finally {
      this.busy = false
      this.events.changed()
    }
  }

  setLoopEnabled(enabled: boolean): void {
    this.loopEnabled = enabled
    this.applyLoop()
  }

  setLoopRegion(start: number, end: number): void {
    const minimum = 480 / 48_000
    let a = clamp(start, 0, VIDEO_SECONDS - minimum)
    let b = clamp(end, a + minimum, VIDEO_SECONDS)
    if (b <= a + minimum) b = a + minimum
    this.loopStart = a
    this.loopEnd = b
    this.applyLoop()
  }

  setSelected(index: number): void {
    this.selected = index
    this.renderer.setSelected(index)
    this.events.changed()
  }

  toggleMute(index: number): void {
    const next = !this.muted[index]
    this.muted[index] = next
    this.audio?.setMute(index, next)
    this.renderer.setMuted(index, next)
    this.events.changed()
  }

  /** Manual A/V trim in milliseconds, for calibrating unusual output paths. */
  setLatencyTrim(milliseconds: number): void {
    if (this.clock) this.clock.latencyTrim = milliseconds / 1000
  }

  async dispose(): Promise<void> {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    cancelAnimationFrame(this.raf)
    this.video?.dispose()
    this.videoElement?.remove()
    await this.audio?.dispose()
  }

  private applyLoop(): void {
    const clock = this.clock
    const wasPlaying = clock?.isPlaying ?? false
    if (wasPlaying) this.pause()
    clock?.setLoop({ start: this.loopStart, end: this.loopEnd }, this.loopEnabled)
    this.video?.setLoop(this.loopStart, this.loopEnd, this.loopEnabled)
    if (wasPlaying) this.play()
    this.events.changed()
  }

  private async setupVideo(): Promise<void> {
    if (this.video) return
    const tiers = selectVideoTiers()

    for (const tier of tiers) {
      const scheduler = await FrameScheduler.tryCreate(tier)
      if (scheduler) {
        scheduler.setLoop(this.loopStart, this.loopEnd, this.loopEnabled)
        this.video = scheduler
        return
      }
    }

    // No WebCodecs, or no tier this browser can decode. Drive an element and
    // phase-lock it instead.
    const element = document.createElement('video')
    element.src = tiers[tiers.length - 1]!.url
    element.preload = 'auto'
    element.muted = true
    element.playsInline = true
    element.crossOrigin = 'anonymous'
    element.setAttribute('disablepictureinpicture', '')
    element.style.cssText = 'position:absolute;width:2px;height:2px;left:-9999px;opacity:.001;pointer-events:none'
    document.body.appendChild(element)
    this.videoElement = element

    await new Promise<void>((resolve) => {
      if (element.readyState >= 2) return resolve()
      element.addEventListener('loadeddata', () => resolve(), { once: true })
      element.addEventListener('error', () => resolve(), { once: true })
      window.setTimeout(resolve, 8000)
    })

    const clock = this.clock
    if (!clock) throw new Error('Clock was not initialised before video setup.')
    const source = new ElementFrameSource(element, (ms) => clock.musicalAtPerformanceTime(ms))
    source.setLoop(this.loopStart, this.loopEnd, this.loopEnabled)
    this.video = source
  }

  private prefetchOtherBanks(): void {
    // Warm the HTTP cache only. Decoding happens on selection, so this costs
    // bandwidth at idle rather than memory.
    window.setTimeout(() => {
      for (const tempo of [...TEMPO_PRESETS].reverse()) {
        if (tempo === this.tempoPercent) continue
        for (const url of stemUrls(tempo)) {
          const link = document.createElement('link')
          link.rel = 'prefetch'
          link.as = 'fetch'
          link.href = url
          link.crossOrigin = 'anonymous'
          document.head.appendChild(link)
        }
      }
    }, 1500)
  }

  private loadPoster(): void {
    const url = posterUrl()
    if (!url) return
    const img = new Image()
    img.decoding = 'async'
    img.addEventListener('load', () => {
      this.poster = img
      this.renderer.invalidate()
    })
    img.src = url
  }

  private startRenderLoop(): void {
    const tick = (now: number) => {
      this.raf = requestAnimationFrame(tick)

      // Track the display refresh interval so the video can be scheduled for
      // when the frame will actually be seen, not when it was drawn.
      if (this.lastRafTime) {
        const delta = (now - this.lastRafTime) / 1000
        if (delta > 0.002 && delta < 0.1) {
          this.refreshInterval = this.refreshInterval * 0.9 + delta * 0.1
        }
      }
      this.lastRafTime = now

      const clock = this.clock
      if (!clock) {
        if (this.poster) this.renderer.draw(this.poster, 0)
        return
      }

      const musical = clock.positionForDisplay(this.refreshInterval)

      // The video path is isolated from painting and from the transport. A
      // decoder failure must never be able to stop the picture updating, stop
      // the clock, or leave the player unable to restart.
      let frame: CanvasImageSource | null = null
      let frameId = 0
      const video = this.video
      if (video) {
        try {
          video.update(musical, clock.currentTempo, clock.isPlaying)
          frame = video.acquire(musical)
          frameId = video.presentedId
        } catch (err) {
          this.noteVideoFailure(err)
        }
      }

      if (frame) {
        this.renderer.draw(frame, frameId)
        this.hasLiveFrame = true
      } else if (this.poster && !this.hasLiveFrame) {
        // The poster is only ever the *initial* image. Once real frames have
        // been shown, a momentary decode stall must leave the last frame on
        // screen — falling back to the poster here would flash the opening
        // image mid-performance.
        this.renderer.draw(this.poster, 0)
      }

      this.events.time(clock.positionAudible())
    }
    this.raf = requestAnimationFrame(tick)
  }

  /**
   * Last-resort handling for a video source that keeps throwing. The scheduler
   * rebuilds its own decoders; if even that fails repeatedly, drop the source
   * entirely so the player falls back to the poster and audio keeps running,
   * rather than presenting a frozen picture with no way back.
   */
  private noteVideoFailure(err: unknown): void {
    this.videoFailures += 1
    if (this.videoFailures < 120) return

    const source = this.video
    this.video = null
    source?.dispose()
    this.videoFailures = 0
    this.events.status(
      `Video playback stopped and could not be recovered: ${
        err instanceof Error ? err.message : String(err)
      }. Audio is unaffected — reload to retry video.`,
      'error',
    )
    this.events.changed()
  }

  private readonly onVisibilityChange = (): void => {
    if (document.hidden && this.clock?.isPlaying) this.pause()
  }
}
